import { BIRTH_DATE_COPY_DE } from "@shared/birthDateCopy";
import type { BookingMessages } from "./types";

export const de: BookingMessages = {
  meta: {
    title: "Stellplatz online anfragen",
    description: "Stellplatz online anfragen – Campingplatz Mainkur am Rhein.",
  },
  common: {
    night: "Nacht",
    nights: "Nächte",
    day: "Tag",
    days: "Tage",
    loading: "Wird geladen …",
    back: "Zurück",
    continue: "Weiter",
    edit: "Bearbeiten",
    selectDate: "Datum wählen",
    arrival: "Anreise",
    departure: "Abreise",
    adults: "Erwachsene",
    adultsAgeHint: "ab 15 Jahren",
    children: "Kinder",
    childrenAgeHint: "2–14 Jahre",
    dogs: "Hunde",
    inclVat: "inkl. MwSt.",
    approx: "Ca.",
    licensePlate: "Kennzeichen",
    dash: "–",
  },
  steps: {
    period: "Zeitraum",
    vehicle: "Fahrzeug",
    pitch: "Platz",
    details: "Reisende",
    contact: "Kontakt",
    summary: "Übersicht",
  },
  stepHeadings: {
    period: {
      title: "Reisezeitraum",
      intro: "Wann möchten Sie anreisen und wieder abreisen?",
    },
    vehicle: {
      title: "Anreise & Fahrzeug",
      intro: "Womit reisen Sie an? Danach richtet sich, welche Stellplätze passen.",
    },
    pitch: {
      title: "Stellplatz wählen",
      intro: "Diese Kategorien stehen in Ihrem Zeitraum zur Verfügung.",
    },
    details: {
      title: "Reisende & Wünsche",
      intro: "Wer reist mit – und gibt es besondere Wünsche zum Platz?",
    },
    contact: {
      title: "Ihre Kontaktdaten",
      intro: "Wir brauchen Ihre Daten für die Bestätigung und mögliche Rückfragen.",
    },
    summary: {
      title: "Zusammenfassung",
      intro: "Bitte prüfen Sie Ihre Angaben, bevor Sie die Anfrage absenden.",
    },
  },
  flow: {
    pageTitle: "Stellplatz online anfragen",
    pageSubtitle: "Unverbindliche Anfrage – Sie erhalten eine Antwort per E-Mail.",
    stepOf: (current, total) => `Schritt ${current} von ${total}`,
    soldOutDaysHint:
      "Ausgebuchte Tage sind im Kalender ausgegraut. Eine Anreise ist dort nicht möglich, eine Abreise schon.",
    vehicleLengthHint: "Gesamtlänge inklusive Anhänger oder Zugfahrzeug.",
    vehicleDetailsTitle: "Angaben zum Fahrzeug",
    noVehicleDetailsNeeded: "Für diese Anreiseart sind keine weiteren Angaben nötig.",
    arrivalTypeUnavailable: (label, arrival, departure) =>
      `Für ${label} ist vom ${arrival} bis ${departure} kein Stellplatz frei. Bitte wählen Sie eine andere Anreiseart oder andere Reisedaten.`,
    categoryLabel: "Kategorie",
    travellersTitle: "Reisende",
    optionalLabel: "optional",
    quickSelect: (min, max) => `Zurück zur Auswahl ${min}–${max}`,
    knownCompanionSelect: "Person aus früheren Aufenthalten übernehmen",
    knownCompanionManual: "Manuell eingeben",
    licensePlateFormatHint: "Leerzeichen und Bindestrich werden automatisch gesetzt.",
    summaryIntro: "Alle Angaben lassen sich über „Bearbeiten“ noch ändern.",
    pitchAvailableHint: "Es sind Stellplätze frei – wählen Sie im nächsten Schritt Ihre Kategorie.",
    thankYouTitle: "Vielen Dank!",
    bookingNumber: "Buchungsnummer",
    configLoadError: "Einstellungen für die Online-Buchung konnten nicht geladen werden.",
    priceCalcError: "Preis konnte nicht berechnet werden.",
    holdCreateError: "Die Reservierung konnte nicht angelegt werden.",
    availabilityLoadError: "Verfügbarkeit konnte nicht geladen werden.",
    alternativesLoadError: "Alternative Zeiträume konnten nicht geladen werden.",
    submitError: "Buchung konnte nicht gesendet werden.",
    staleAlternative:
      "Dieser Termin ist inzwischen nicht mehr frei. Bitte wählen Sie einen anderen Vorschlag oder andere Reisedaten.",
    holdExpired: "Ihre Reservierung ist abgelaufen. Bitte wählen Sie erneut eine Platzkategorie.",
    selectCategory: "Bitte wählen Sie eine Platzkategorie.",
    noPitchWithSuggestions:
      "In diesem Zeitraum ist kein Stellplatz frei. Bitte wählen Sie einen der Vorschläge oder andere Reisedaten.",
    noPitch:
      "Für den gewählten Zeitraum sind keine Stellplätze verfügbar. Bitte wählen Sie andere Reisedaten.",
    noPitchForArrival:
      "Für diese Anreiseart ist aktuell kein Stellplatz verfügbar. Bitte wählen Sie eine andere Anreiseart oder andere Reisedaten.",
    selectAvailablePeriod: "Bitte wählen Sie einen freien Zeitraum für diese Kategorie.",
    acceptTerms: "Bitte bestätigen Sie die Buchungsbedingungen.",
    savedData: "Ihre gespeicherten Daten",
    contactData: "Ihre Kontaktdaten",
    missingFieldsHint: "Bitte ergänzen Sie die fehlenden Angaben.",
    savedProfileHint:
      "Wir übernehmen Ihre gespeicherten Daten. Fehlende Pflichtangaben können Sie unten ergänzen.",
    yourDataField: (field: string) => `Ihre Daten: ${field}`,
    knownCompanionHint:
      "Mitreisende aus früheren Aufenthalten können Sie direkt übernehmen.",
    companionsSummary: "Mitreisende",
    companionDetailsHint:
      "Angaben zu den weiteren Reisenden. Sie selbst sind als Hauptreisende/r bereits erfasst.",
    sending: "Wird gesendet …",
    submitRequest: "Anfrage absenden",
    checkingAvailability: "Verfügbarkeit wird geprüft …",
    loadingCategories: "Kategorien werden geladen …",
    selectPeriodFirst: "Zuerst freien Zeitraum wählen",
    selectPitch: "Stellplatz wählen",
    checkAvailability: "Verfügbarkeit prüfen",
    messageLabel: "Nachricht an uns",
    messagePlaceholder: "z. B. voraussichtliche Ankunftszeit oder besondere Wünsche",
    termsPrefix: "Ich habe die",
    termsAnd: "und die",
    termsSuffix:
      "zur Kenntnis genommen und sende eine unverbindliche Buchungsanfrage.",
    termsLink: "Stornobedingungen",
    privacyLink: "Datenschutzhinweise",
    legalClose: "Schließen",
    partyTitle: "Aufenthalt",
    agesTitle: "Alter der Reisenden",
    agesHint: "Kinder sind 2–14 Jahre alt, Erwachsene ab 15 Jahren.",
    primaryGuestAge: "Alter Hauptreisende/r",
    adultAge: (n) => `Alter Erwachsene/r ${n}`,
    childAge: (n) => `Alter Kind ${n}`,
    addonsTitle: "Zusatzleistungen",
    featuresTitle: "Wünsche zum Stellplatz",
    featuresHint: "Unverbindlich – wir berücksichtigen Wünsche nach Verfügbarkeit.",
    summaryTitle: "Zusammenfassung",
    summaryPeriod: "Zeitraum",
    summaryVehicle: "Stellplatz & Anreise",
    summaryPitch: "Stellplatz",
    summaryParty: "Reisende",
    summaryContact: "Kontakt",
    summaryMessage: "Nachricht",
    summaryPrice: "Preis",
    next: "Weiter",
    previous: "Zurück",
  },
  validation: {
    selectDates: "Bitte An- und Abreisedatum wählen.",
    departureAfterArrival: "Abreise muss nach der Anreise liegen.",
    arrivalNotPast: "Anreise darf nicht in der Vergangenheit liegen.",
    sameDayCutoffBlocked:
      "Für Anreisen am heutigen Tag sind Online-Buchungen ab {time} Uhr nicht mehr möglich.",
    minStayOne: "Mindestens 1 Nacht ist erforderlich.",
    minStayMany: (n) => `Für den gewählten Zeitraum sind mindestens ${n} Nächte erforderlich.`,
    soldOutArrival:
      "Der Campingplatz ist an diesem Tag leider ausgebucht. Anreise an diesem Tag ist nicht möglich.",
    licensePlate: "Bitte prüfen Sie das Kennzeichen (Beispiel: AB-C 1234).",
    ageRequired: "Bitte geben Sie das Alter aller Reisenden an.",
    childAgeRange: "Kinder müssen am Anreisetag zwischen 2 und 14 Jahre alt sein.",
    childAgeMin: "Kinder müssen am Anreisetag mindestens 2 Jahre alt sein.",
    adultAgeMin: "Erwachsene müssen am Anreisetag mindestens 15 Jahre alt sein.",
    primaryAgeMin: "Hauptreisende/r muss am Anreisetag mindestens 18 Jahre alt sein.",
    primaryBirthDateRequired: "Bitte geben Sie das Geburtsdatum der Hauptreisenden an.",
    companionBirthDateRequired: (slot) => `${slot}: Geburtsdatum ist erforderlich.`,
    invalidBirthDate: "Bitte prüfen Sie das Geburtsdatum.",
    companionInvalidBirthDate: (slot) => `${slot}: Bitte prüfen Sie das Geburtsdatum.`,
    companionFieldRequired: (slot, field) => `${slot}: ${field} ist erforderlich.`,
    primaryGuestRequiredFields: (fields) =>
      `Bitte füllen Sie folgende Pflichtfelder aus: ${fields}.`,
    emailIncomplete: "Bitte prüfen Sie die E-Mail-Adresse – das Format wirkt unvollständig.",
    emailUnusual: "Bitte prüfen Sie die E-Mail-Adresse – das Format wirkt ungewöhnlich.",
  },
  birthDate: BIRTH_DATE_COPY_DE,
  availability: {
    checkingPhrases: [
      "Verfügbarkeit wird geprüft …",
      "Passende Stellplätze werden gesucht …",
      "Belegung wird abgeglichen …",
    ],
    alternativePhrases: [
      "Freie Termine werden gesucht …",
      "Die nächsten 14 Tage werden geprüft …",
    ],
    searchingPitches: "Wir prüfen, welche Stellplätze in Ihrem Zeitraum frei sind.",
    searchingAlternatives: "Wir prüfen freie Termine in den nächsten 14 Tagen.",
    periodSoldOutTitle: "In diesem Zeitraum ist kein Stellplatz frei",
    periodSoldOutBody: (arrival, departure, nights, nightLabel) =>
      `Vom ${arrival} bis ${departure} (${nights} ${nightLabel}) können wir keinen Stellplatz anbieten.`,
    periodSoldOutHint: "Bitte wählen Sie einen anderen Zeitraum.",
    tryWithoutElectricity: "Ohne Stromanschluss prüfen",
    soldOutTitle: "In diesem Zeitraum ist alles belegt",
    soldOutBody: (arrival, departure, nights, nightLabel) =>
      `Vom ${arrival} bis ${departure} (${nights} ${nightLabel}) sind alle Stellplätze vergeben.`,
    soldOutHint:
      "Unten sehen Sie freie Termine in der Nähe Ihres Wunschzeitraums. Alternativ wählen Sie andere Reisedaten.",
    noAlternativesTitle: (category) =>
      category
        ? `Für „${category}“ ist in der Nähe kein Termin frei`
        : "In der Nähe ist kein Termin frei",
    noAlternativesBody: (category) =>
      category
        ? `Für „${category}“ ist in den nächsten 14 Tagen nichts frei.`
        : "In den nächsten 14 Tagen ist nichts frei.",
    noVehiclePitchTitle: "Kein passender Stellplatz für Ihr Fahrzeug",
    noVehiclePitchBody: (vehicleLabel) =>
      vehicleLabel
        ? `Für ${vehicleLabel} haben wir im gewählten Zeitraum keinen geeigneten Stellplatz.`
        : "Für Ihr Fahrzeug haben wir im gewählten Zeitraum keinen geeigneten Stellplatz.",
    noVehiclePitchHint:
      "Bitte prüfen Sie die Fahrzeugangaben oder wählen Sie einen anderen Zeitraum.",
    alternativesTitle: (category) =>
      category ? `Freie Termine für „${category}“` : "Freie Termine in der Nähe",
    alternativesHint: (nights, nightLabel) =>
      `Gewünscht: ${nights} ${nightLabel}. Mit einem Klick übernehmen Sie den Termin.`,
    selectPeriod: "Termin übernehmen",
    sameArrival: "Gleiche Anreise",
    sameDeparture: "Gleiche Abreise",
    arrivalLater: (days, unit) => `Anreise ${days} ${unit} später`,
    arrivalEarlier: (days, unit) => `Anreise ${days} ${unit} früher`,
    periodLater: (days, unit) => `Zeitraum ${days} ${unit} später`,
    periodEarlier: (days, unit) => `Zeitraum ${days} ${unit} früher`,
    samePeriod: "Gleicher Zeitraum",
    nightsWanted: (nights, wanted) =>
      `${nights} ${nights === 1 ? "Nacht" : "Nächte"} (${wanted} gewünscht)`,
    placeFree: "Platz frei",
    placesFree: "Plätze frei",
    inCategory: " in dieser Kategorie",
    choosePeriod: "Bitte wählen Sie im Kalender einen anderen Zeitraum.",
  },
  arrivalType: {
    legend: "Anreiseart",
    noneAvailable:
      "Für die Online-Buchung sind derzeit keine Anreisearten freigegeben. Bitte wenden Sie sich an den Campingplatz.",
  },
  electricity: {
    legend: "Stromanschluss",
    ariaLabel: "Stromanschluss wählen",
    withLabel: "Mit Stromanschluss",
    withHint: "CEE-Steckdose am Stellplatz",
    withoutLabel: "Ohne Stromanschluss",
    withoutHint: "Kein Strom am Platz benötigt",
  },
  price: {
    calculating: "Preis wird berechnet…",
    overview: "Preisübersicht",
    estimatedTotal: "Geschätzter Gesamtpreis",
    perNight: "/ Nacht",
    positionTotal: "Gesamt",
    lineNightFormula: (qty, nights, nightLabel, unitPrice) =>
      `${qty} × ${nights} ${nightLabel} × ${unitPrice}`,
    lineUnitFormula: (qty, unitPrice) => `${qty} × ${unitPrice}`,
    disclaimer:
      "Voraussichtlicher Preis inkl. MwSt. Der endgültige Betrag wird mit der Bestätigung festgelegt.",
    pending: "Preis folgt",
    pendingHint:
      "Für diesen Zeitraum sind die Preise noch nicht veröffentlicht. Sie können trotzdem buchen. Den Preis teilen wir Ihnen mit, sobald er feststeht.",
  },
  guestAuth: {
    portal: "Mein Gästeportal",
    returningGuests: "Wiederkehrende Gäste",
    alreadyBooked: "Schon einmal bei uns gewesen?",
    alreadyBookedHint:
      "Melden Sie sich an, um Ihre gespeicherten Daten und frühere Mitreisende zu übernehmen.",
    login: "Anmelden",
    loginCodeHint: "Sie erhalten einen Anmeldecode per E-Mail.",
    manageStays: "Aufenthalte verwalten",
    manageStaysHint:
      "Melden Sie sich an, um kommende und vergangene Aufenthalte einzusehen.",
    loginNow: "Jetzt anmelden",
    returningGuestsRegion: "Anmeldung für wiederkehrende Gäste",
    inlineRegion: "Hinweis für wiederkehrende Gäste",
  },
  login: {
    title: "Anmelden",
    subtitleBooking:
      "Schon einmal bei uns? Melden Sie sich mit Ihrer E-Mail an – Ihre Daten werden in der Buchung übernommen.",
    subtitleDefault:
      "Melden Sie sich mit Ihrer E-Mail-Adresse an. Wir senden Ihnen einen einmaligen Code.",
    emailLabel: "E-Mail-Adresse",
    codeLabel: "Anmeldecode",
    requestCode: "Code per E-Mail anfordern",
    requestingCode: "Wird gesendet …",
    verify: "Anmelden",
    verifying: "Wird geprüft …",
    backToBooking: "Zurück zur Buchung",
    toBooking: "Zur Online-Buchung",
    requestError: "Code konnte nicht angefordert werden.",
    loginError: "Anmeldung fehlgeschlagen.",
  },
  portal: {
    title: "Mein Gästeportal",
    loadError: "Daten konnten nicht geladen werden.",
    sectionsAria: "Portal-Bereiche",
    upcoming: "Kommende Aufenthalte",
    upcomingEmpty:
      "Keine geplanten Aufenthalte. Buchen Sie Ihren nächsten Urlaub direkt online.",
    past: "Vergangene Aufenthalte",
    pastEmpty: "Noch keine vergangenen Aufenthalte.",
    profile: "Meine Daten",
    logout: "Abmelden",
    bookNow: "Neue Reservierung",
  },
  bookingDetail: {
    loadError: "Aufenthalt konnte nicht geladen werden.",
    backToPortal: "← Zurück zu Meine Aufenthalte",
  },
  fields: {
    title: "Anrede",
    first_name: "Vorname",
    middle_name: "Zweiter Vorname",
    last_name: "Nachname",
    company_name: "Firmenname",
    vat_id: "Umsatzsteuer-ID",
    email: "E-Mail",
    phone: "Telefon",
    license_plate: "Kennzeichen",
    age: "Alter",
    date_of_birth: "Geburtsdatum",
    nationality: "Nationalität",
    street: "Straße",
    house_number: "Hausnummer",
    house_number_short: "Nr.",
    address_search: "(Suche)",
    address_placeholder: "Adresse eingeben und Vorschlag wählen …",
    address_line_2: "Adresszusatz",
    postal_code: "PLZ",
    city: "Ort",
    state_region: "Bundesland/Kanton",
    arrival: "Anreise",
    departure: "Abreise",
    nights: "Nächte",
    travelers: "Reisende",
    arrival_type: "Anreiseart",
    electricity: "Strom",
    travel_data: "Reisedaten",
    price: "Preis",
    your_message: "Ihre Nachricht",
    main_guest: "Hauptreisende/r",
    pitch: "Stellplatz",
    yes: "Ja",
    no: "Nein",
  },
  vehicleFields: {
    license_plate: "Kennzeichen",
    vehicle_length_m: "Länge",
    vehicle_width_m: "Breite",
    vehicle_height_m: "Höhe",
    vehicle_weight_kg: "Gewicht",
    tent_size: "Zeltgröße",
    vehicle_length_placeholder: "z. B. 7,5",
    tent_size_placeholder: "z. B. 4 × 6 m",
  },
  companionFields: {
    title: "Anrede",
    first_name: "Vorname",
    middle_name: "Zweiter Vorname",
    last_name: "Nachname",
    company_name: "Firmenname",
    vat_id: "Umsatzsteuer-ID",
    email: "E-Mail",
    phone: "Telefon",
    license_plate: "Kennzeichen",
    age: "Alter",
    date_of_birth: "Geburtsdatum",
    nationality: "Nationalität",
    street: "Straße",
    house_number: "Hausnummer",
    house_number_short: "Nr.",
    address_search: "(Suche)",
    address_placeholder: "Adresse eingeben und Vorschlag wählen …",
    address_line_2: "Adresszusatz",
    postal_code: "PLZ",
    city: "Ort",
    state_region: "Bundesland/Kanton",
    country_code: "Land (ISO)",
    adult: "Erwachsene/r",
    child: "Kind",
    no_details: "Keine Angaben",
  },
  salutation: {
    herr: "Herr",
    frau: "Frau",
    select: "Bitte wählen …",
  },
  bookingStatus: {
    draft: "Entwurf",
    requested: "Anfrage",
    reserved: "Reserviert",
    confirmed: "Bestätigt",
    checked_in: "Eingecheckt",
    checked_out: "Abgereist",
    cancelled: "Storniert",
    rejected: "Abgelehnt",
    no_show: "Nicht erschienen",
  },
  guestPortalVehicle: {
    motorhome: "Wohnmobil",
    caravan: "Wohnwagen",
    tent_car: "Zelt mit Auto",
    tent_bike: "Zelt mit Fahrrad",
    kommend: "Kommend",
    aktuell: "Aktuell",
    vergangen: "Vergangen",
    storniert: "Storniert",
    electricity_yes: "Ja",
    electricity_no: "Nein",
  },
  season: {
    outsideSeason: "Der gewählte Zeitraum liegt außerhalb der Saison.",
    bookableOnly: "Online buchbar während der Saison",
    closingDayDepartureOnly: "Am letzten Saisontag ist nur die Abreise möglich.",
  },
  periodSelected: {
    title: "Zeitraum ausgewählt",
    nights: (n, nightLabel) => `${n} ${nightLabel}`,
  },
  holdTimer: {
    label: "Reserviert für",
    hint: "So lange halten wir den Stellplatz für Sie frei.",
    expired: "Ihre Reservierung ist abgelaufen. Bitte wählen Sie erneut eine Platzkategorie.",
  },
  category: {
    recommended: "Empfohlen",
    from: "ab",
    perNight: "/ Nacht",
    perPeriod: {
      night: "/ Nacht",
      week: "/ Woche",
      month: "/ Monat",
      quarter: "/ Quartal",
      semi_annual: "/ Halbjahr",
      annual: "/ Jahr",
    },
    priceHint: "inkl. MwSt., 1 Erwachsener",
    total: "Gesamt",
    priceFollows: "Preis folgt",
    select: "Auswählen",
    selected: "Ausgewählt",
    unavailable: "Nicht verfügbar",
    available: (count) => (count === 1 ? `${count} Platz frei` : `${count} Plätze frei`),
  },
  phoneComplete: {
    pageTitle: "Reservierung bestätigen",
    pageSubtitle:
      "Bitte tragen Sie Ihre vollständigen Daten ein und bestätigen Sie die Buchung verbindlich.",
    loading: "Laden…",
    invalidLink: "Der Link ist ungültig oder abgelaufen.",
    toBooking: "Zur Buchungsseite",
    alreadyDone: "Diese Buchung wurde bereits verbindlich bestätigt. Vielen Dank!",
    confirmed:
      "Vielen Dank – Sie haben Ihre Buchung verbindlich bestätigt. Wir freuen uns auf Ihren Besuch.",
    booking: "Buchung",
    period: "Zeitraum",
    category: "Kategorie",
    deadline: (when) =>
      `Bitte bis ${when} bestätigen – danach wird der Platz freigegeben.`,
    yourData: "Ihre Daten",
    electricity: "Stromanschluss gewünscht",
    wishes: "Wünsche zum Stellplatz",
    wishesHint: "Wir berücksichtigen Ihre Wünsche, sofern verfügbar.",
    vehicle: "Fahrzeug",
    notes: "Anmerkungen (optional)",
    firstNameRequired: "Bitte geben Sie Ihren Vornamen an.",
    lastNameRequired: "Bitte geben Sie Ihren Nachnamen an.",
    arrivalType: "Anreiseart",
    arrivalTypeRequired: "Bitte wählen Sie die Anreiseart.",
    categoryRequired: "Bitte wählen Sie eine Platzkategorie.",
    vehicleRequired: "Bitte füllen Sie alle Pflichtfelder zum Fahrzeug aus.",
    saveError: "Speichern fehlgeschlagen. Bitte erneut versuchen.",
    saving: "Wird gespeichert…",
    submit: "Buchung verbindlich bestätigen",
    submitAndPay: "Angaben speichern und Anzahlung leisten",
    bookingNumber: "Buchungsnummer",
    termsPrefix: "Ich bestätige diese Buchung verbindlich. Ich habe die",
    termsSuffix: "zur Kenntnis genommen.",
    depositTitle: "Anzahlung",
    depositOnline: "Nach dem Speichern leiten wir Sie zur Online-Zahlung der Anzahlung weiter.",
    depositOffline: (amount) =>
      `Die Anzahlung von ${amount} wird nach den Zahlungsbedingungen fällig. Eine Online-Zahlung ist derzeit nicht eingerichtet.`,
    depositEstimate: (label, amount) => `${label}: ${amount}`,
    payDeposit: "Anzahlung jetzt zahlen",
    paying: "Weiter zur Zahlung…",
    paymentCancelled: "Die Zahlung wurde abgebrochen. Sie können die Anzahlung erneut starten.",
    paymentPending:
      "Die Anzahlung ist noch nicht bestätigt. Bitte laden Sie die Seite gleich neu oder wenden Sie sich an die Rezeption.",
    confirmedDepositDue: (amount) =>
      `Vielen Dank. Ihre Buchung ist bestätigt. Bitte beachten Sie die Anzahlung von ${amount}.`,
    confirmedPaid: "Vielen Dank. Ihre Angaben sind gespeichert und die Anzahlung ist eingegangen.",
  },
  comfortCheckIn: {
    pageTitle: "Comfort Check-in",
    pageSubtitle: "Kennzeichen angeben und den Aufenthalt online bezahlen.",
    loading: "Laden…",
    invalidLink: "Dieser Comfort-Check-in-Link ist ungültig.",
    toBooking: "Zur Buchungsseite",
    booking: "Buchung",
    period: "Zeitraum",
    pitch: "Platz",
    guest: "Gast",
    stayTotal: "Aufenthaltspreis",
    amountDue: "Jetzt zu zahlen",
    alreadyPaid: "Der Aufenthalt ist bereits bezahlt. Bitte bestätigen Sie nur noch das Kennzeichen.",
    pricePending:
      "Für diesen Aufenthalt ist noch kein Preis veröffentlicht. Sie können den Check-in abschließen, der Preis wird nachgereicht.",
    priceFollows: "Preis folgt",
    licensePlate: "KFZ-Kennzeichen",
    licensePlateCountry: "Land",
    plateRequired: "Bitte geben Sie das KFZ-Kennzeichen an.",
    pay: "Kennzeichen speichern und bezahlen",
    completeFree: "Comfort Check-in abschließen",
    submitting: "Wird vorbereitet…",
    redirecting: "Weiter zur Zahlung…",
    completed: "Vielen Dank. Ihr Comfort Check-in ist abgeschlossen. Wir freuen uns auf Ihre Anreise.",
    alreadyDone: "Der Comfort Check-in für diese Buchung ist bereits abgeschlossen.",
    bookingNumber: "Buchungsnummer",
    paymentCancelled: "Die Zahlung wurde abgebrochen. Sie können es erneut versuchen.",
    confirmingPayment: "Zahlung wird bestätigt…",
    paymentPending:
      "Die Zahlung ist bei uns noch nicht bestätigt. Bitte laden Sie die Seite in einem Moment neu oder wenden Sie sich an die Rezeption.",
    stripeUnavailable:
      "Online-Zahlung ist derzeit nicht verfügbar. Bitte wenden Sie sich an die Rezeption.",
    saveError: "Der Comfort Check-in konnte nicht gespeichert werden. Bitte erneut versuchen.",
  },
};
